import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import moment from "moment-timezone";
import { useEffect, useState, type FC } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { z } from "zod";

import { ContentPad, Utility } from "../../app/app-layout";
import { states } from "../../data/states";
import { useCityAndState } from "../../hooks/use-azure-maps-city-state";
import {
  defaultFamilyContactDetail,
  FamilyContact,
  useFamilyConstants,
  useFamilyContactDetail,
  type FamilyContactDetailWithId
} from "../../hooks/use-families";
import { InquiryFormData } from "../../hooks/use-inquiries";
import { FormHelpers } from "../forms";
import { isValidOptionalPhoneNumber, zipcodeSchema } from "../forms/form-helpers";
import { InputField } from "../forms/input-field";
import { PhoneNumberField } from "../forms/phone-number-field";
import { SelectField } from "../forms/select-field";
import { useDuplicateContactModal } from "./use-duplicate-contact-modal";

type InquiryFormDataWithFamilyDetails = InquiryFormData & {
  student: z.input<typeof quickCreateStudentFormSchema>;
};

export const QuickCreateStudent: FC<{
  onSave: (contact: FamilyContactDetailWithId) => void;
  onCancel: () => void;
  inquirer?: FamilyContact;
  student?: FamilyContact;
}> = ({ onCancel, onSave, inquirer, student }) => {
  const { activeGrades, activeTitles, activeGenders } = useFamilyConstants();

  const { contactDetail: inquirerDetail } = useFamilyContactDetail("inquirer", inquirer?.id);

  const { contactDetail: studentDetail, createOrUpdateContactMutator } = useFamilyContactDetail("student", student?.id);

  const {
    register,
    reset,
    watch,
    setValue,
    trigger,
    getValues,
    handleSubmit,
    formState: { errors, isSubmitted, isDirty }
  } = useQuickCreateStudentForm();
  const { setValue: setInquiryFormValue, getValues: getInquiryFormValues } =
    useFormContext<InquiryFormDataWithFamilyDetails>();
  const { renderDuplicateContactModal, onSubmitError } = useDuplicateContactModal({ onSubmit: onSave });

  useEffect(() => {
    const values = getValues();
    const inquiryStudentValues = getInquiryFormValues("student");
    setInquiryFormValue(
      "student",
      {
        ...inquiryStudentValues,
        ...values
      },
      { shouldDirty: true }
    );
  }, [isDirty]);

  useEffect(() => {
    reset(studentDetail || defaultFamilyContactDetail);
  }, [studentDetail]);

  // istanbul ignore next
  const handleSave = handleSubmit(formValues => {
    createOrUpdateContactMutator.mutate(
      { id: student?.id, payload: formValues },
      { onSuccess: onSave, onError: onSubmitError }
    );
  });

  // TODO: Figure out reusable generic way to autofill city and state in any form with relevant fields
  const zipcode = watch("zipcode");
  const cityAndStateQuery = useCityAndState(zipcode, {
    enabled: FormHelpers.isValidZipcode(zipcode) && (!watch("city").length || !watch("state").length)
  });

  const [useSameAddressAsInquirer, setUseSameAddressAsInquirer] = useState(false);

  useEffect(() => {
    if (cityAndStateQuery?.data?.city && cityAndStateQuery?.data?.state) {
      setValue("city", cityAndStateQuery.data.city);
      setValue("state", cityAndStateQuery.data.state);
    }
  }, [cityAndStateQuery.data]);

  useEffect(() => {
    const fieldsToValidate = Object.entries({
      // Always validate these fields on submit
      homePhone: true,
      businessPhone: true,
      personalPhone: true,
      // Validate these fields only if they have been filled out
      email: isSubmitted,
      contact_root: isSubmitted
    })
      .filter(([_, validate]) => validate)
      .map(([field]) => field as keyof QuickCreateStudentFormValuesInput);

    trigger(fieldsToValidate);
  }, [isSubmitted, ...watch(["email", "homePhone", "personalPhone"])]);

  const dob = watch("dob");
  useEffect(() => {
    if (dob) {
      setValue("age", moment().diff(dob, "years"), { shouldValidate: true });
    }
  }, [dob]);

  const handleSameAddressChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const checked = e.target.checked;
    setUseSameAddressAsInquirer(checked);
    if (checked) {
      setValue("addressLine1", inquirerDetail?.addressLine1 ?? "");
      setValue("addressLine2", inquirerDetail?.addressLine2 ?? "");
      setValue("zipcode", inquirerDetail?.zipcode ?? "");
      setValue("city", inquirerDetail?.city ?? "");
      setValue("state", inquirerDetail?.state ?? "");
    }
  };

  return (
    <>
      <div className="sticky-top">
        <Utility>
          <div className="d-flex  stack-sm-down w-100 justify-content-end">
            <button className="btn btn-secondary me-2" onClick={onCancel}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              Save Student
            </button>
          </div>
        </Utility>
      </div>

      <ContentPad>
        <h2 className="header-small">Student</h2>
        <div className="card">
          <div className="card-body">
            <div className="row">
              <div className="col col-12 col-md-6">
                <SelectField disableColClass={true} label="Salutation" {...register("title")}>
                  <option value="">Select Salutation</option>
                  {activeTitles?.map(({ value, name }) => (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  ))}
                </SelectField>
              </div>
              <div className="col col-12 col-md-6 col-lg-12">
                <InputField
                  disableColClass={true}
                  label="First Name"
                  error={errors.firstName}
                  {...register("firstName")}
                />
              </div>
              <div className="col col-12 col-md-6 col-lg-12">
                <InputField
                  disableColClass={true}
                  label="Last Name"
                  error={errors.lastName}
                  {...register("lastName")}
                />
              </div>
              <div className="col col-12 col-md-6">
                <InputField disableColClass={true} label="Suffix" error={errors.suffix} {...register("suffix")} />
              </div>
            </div>

            <h3 className="card-subheader">Info</h3>
            <div className="row">
              <div className="col col-6">
                <SelectField disableColClass={true} label="Gender" {...register("gender")}>
                  <option value="">Select Gender</option>
                  {activeGenders?.map(({ value, name }) => (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  ))}
                </SelectField>
              </div>
              <div className="col col-6">
                <InputField
                  disableColClass={true}
                  inputMode="numeric"
                  label="Date of Birth"
                  type="date"
                  error={errors.dob}
                  {...register("dob")}
                />
              </div>
              <div className="col col-6">
                <InputField
                  disableColClass={true}
                  label="Age"
                  inputMode="numeric"
                  min={1}
                  max={99}
                  type="number"
                  error={errors.age}
                  disabled={Boolean(dob)}
                  {...register("age")}
                />
              </div>
              <div className="col col-6">
                <SelectField
                  disableColClass={true}
                  label="Student Grade"
                  isRequired
                  error={errors.gradeId}
                  {...register("gradeId")}
                >
                  <option value="">Select Grade</option>
                  {activeGrades?.map(({ value, name }) => (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  ))}
                </SelectField>
              </div>
              <div className="col col-12">
                <InputField
                  disableColClass={true}
                  label="School"
                  error={errors.schoolName}
                  {...register("schoolName")}
                  readOnly
                />
              </div>
            </div>

            <h3
              className={classNames("card-subheader", {
                "is-invalid": errors["contact_root"]
              })}
            >
              Contact
            </h3>
            {!!errors["contact_root"] && (
              // @ts-expect-error
              <div className="invalid-feedback"> {errors["contact_root"]?.message}</div>
            )}
            <div className="row">
              <div className="col col-12">
                <InputField
                  label="Email"
                  disableColClass={true}
                  validationState={errors.email || errors.contact_root ? "error" : undefined}
                  error={errors.email}
                  {...register("email")}
                />
              </div>
              <div className="col col-12">
                <PhoneNumberField
                  label="Home Phone"
                  disableColClass={true}
                  validationState={errors.homePhone || errors.contact_root ? "error" : undefined}
                  error={errors.homePhone}
                  {...register("homePhone")}
                />
              </div>
              <div className="col col-12">
                <PhoneNumberField
                  label="Mobile Phone"
                  disableColClass={true}
                  validationState={errors.personalPhone || errors.contact_root ? "error" : undefined}
                  error={errors.personalPhone}
                  {...register("personalPhone")}
                />
              </div>
            </div>

            <h3 className="card-subheader">Address</h3>
            <div className="row">
              <div className="col col-12">
                <div className="form-check">
                  <label className="form-check-label" htmlFor="sameaddress">
                    Address is the same as the Initial Inquirer
                  </label>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    onChange={handleSameAddressChange}
                    checked={useSameAddressAsInquirer}
                    id="sameaddress"
                  />
                </div>
              </div>
              <div className="col col-12">
                <InputField
                  label="Address Line 1"
                  disableColClass={true}
                  error={errors.addressLine1}
                  {...register("addressLine1")}
                  disabled={useSameAddressAsInquirer}
                />
              </div>
              <div className="col col-12">
                <InputField
                  label="Address Line 2"
                  disableColClass={true}
                  error={errors.addressLine2}
                  {...register("addressLine2")}
                  disabled={useSameAddressAsInquirer}
                />
              </div>
              <div className="col col-12">
                <InputField
                  label="ZIP"
                  disableColClass={true}
                  error={errors.zipcode}
                  {...register("zipcode")}
                  disabled={useSameAddressAsInquirer}
                />
              </div>
              <div className="col col-12">
                <InputField
                  label="City"
                  disableColClass={true}
                  error={errors.city}
                  {...register("city")}
                  disabled={useSameAddressAsInquirer}
                />
              </div>
              <div className="col col-12">
                <SelectField
                  disabled={useSameAddressAsInquirer}
                  label="State"
                  {...register("state")}
                  disableColClass={true}
                >
                  <option value="">Select State</option>
                  {states.map(({ abbreviation, name }) => (
                    <option key={abbreviation} value={abbreviation}>
                      {name}
                    </option>
                  ))}
                </SelectField>
              </div>
            </div>
          </div>
        </div>
        {renderDuplicateContactModal()}
      </ContentPad>
    </>
  );
};

const nameRegex = /^[a-zA-Z\s\-']+$/;
const suffixRegex = /^[a-zA-Z\s'\-.]+$/;
const addressRegex = /^[a-zA-Z\s'\-.0-9#]+$/;

const invalidCharactersMessage = "contains invalid characters";

export const quickCreateStudentFormSchema = z
  .object({
    title: z.string(),
    firstName: z
      .string()
      .max(20, { message: "must be 20 characters or less" })
      .regex(nameRegex, invalidCharactersMessage)
      .trim()
      .or(z.literal("")),
    lastName: z
      .string()
      .max(30, { message: "must be 30 characters or less" })
      .regex(nameRegex, invalidCharactersMessage)
      .trim()
      .or(z.literal("")),
    suffix: z.string().regex(suffixRegex, invalidCharactersMessage).trim().or(z.literal("")),
    gender: z.string(),
    dob: z
      .string()
      .refine(
        date => {
          const age = moment().diff(date, "years");
          return (date && age >= 1 && age <= 99) || !date;
        },
        { message: "invalid date of birth" }
      )
      .transform(date => (date ? moment(date).format("YYYY-MM-DD") : "")),
    age: z
      .literal("")
      .transform(() => undefined)
      .or(z.coerce.number())
      .optional()
      .refine(age => age === null || age === undefined || (age >= 1 && age <= 99), { message: "invalid age" }),

    gradeId: z.string().min(1, "required"),
    schoolName: z.string().regex(addressRegex, invalidCharactersMessage).trim().or(z.literal("")),
    email: z.string().trim().email("invalid email").or(z.literal("")),
    homePhone: z.string().trim().refine(isValidOptionalPhoneNumber, { message: "invalid phone number" }),
    personalPhone: z.string().trim().refine(isValidOptionalPhoneNumber, { message: "invalid phone number" }),
    addressLine1: z.string().regex(addressRegex, invalidCharactersMessage).trim().or(z.literal("")),
    addressLine2: z.string().regex(addressRegex, invalidCharactersMessage).trim().or(z.literal("")),
    zipcode: zipcodeSchema.or(z.literal("")),
    city: z.string().regex(nameRegex, invalidCharactersMessage).trim().or(z.literal("")),
    state: z.string().trim(),
    // TODO: thoughts on sending some of these props grouped together instead of a flattened object?
    // or grouping them up at least on the front end and transforming before sending them out
    // then we wouldnt have to add these random props
    contact_root: z.any()
  })
  .transform(({ age, ...rest }) => ({ ...rest, ...(age ? { age } : {}) }));

export type QuickCreateStudentFormValuesInput = z.input<typeof quickCreateStudentFormSchema>;
export type QuickCreateStudentFormValues = z.infer<typeof quickCreateStudentFormSchema>;

const useQuickCreateStudentForm = () =>
  useForm<QuickCreateStudentFormValuesInput, never, QuickCreateStudentFormValues>({
    defaultValues: defaultFamilyContactDetail,
    resolver: zodResolver(quickCreateStudentFormSchema)
  });
